/**
 * ReactionPicker — the emoji picker for a message (PR-4, 2026-09-09). A
 * body-portaled popover placed beside its anchor (above when it fits, else
 * below; kept inside the pane's bounds — `pickerPlacement.ts`), showing the
 * quick row first and the grid behind "more". Escape or an outside click
 * closes it. Pure presentational: the host owns what a pick does.
 *
 * Body-portal contract (styles.ts BODY_PORTAL_Z_INDEX doc + portalClickGuard):
 * the card sits at BODY_PORTAL_Z_INDEX — never a bespoke number — and wears
 * `data-prest0n-portal="true"`. The first cut shipped at 10050, below the
 * docked pane (14500), the floating overlay (500000) and fullscreen
 * (9999999): the picker opened BEHIND the chat everywhere, and a click inside
 * it read as "outside the chat" (Miles, 2026-09-09, screenshot).
 */
import React from 'react';
import { createPortal } from 'react-dom';
import { REACTION_EMOJI_GRID, REACTION_EMOJI_QUICK } from '../../constants/reactionEmoji';
import { BODY_PORTAL_Z_INDEX } from './styles';
import { placeReactionPicker, type PickerRect } from '../../services/messageReactions/pickerPlacement';

export interface ReactionPickerProps {
  /** Screen point the picker anchors to (a button's rect or the long-press point). */
  anchor: { x: number; y: number; width?: number; height?: number };
  /** The pane the picker belongs to (the chat container's rect); the card never leaves it. */
  bounds?: PickerRect | null;
  /** The viewer's current reactions on the message — rendered as selected. */
  selected: ReadonlyArray<string>;
  onPick: (emoji: string) => void;
  onClose: () => void;
}

export function ReactionPicker({ anchor, bounds, selected, onPick, onClose }: ReactionPickerProps): React.ReactElement | null {
  const cardRef = React.useRef<HTMLDivElement>(null);
  const [placement, setPlacement] = React.useState<{ top: number; left: number } | null>(null);
  const [showGrid, setShowGrid] = React.useState(false);

  React.useLayoutEffect(() => {
    const card = cardRef.current;
    if (!card) return;
    const c = card.getBoundingClientRect();
    const next = placeReactionPicker(
      anchor,
      { width: c.width, height: c.height },
      { width: window.innerWidth, height: window.innerHeight },
      bounds ?? null,
    );
    setPlacement({ top: next.top, left: next.left });
  }, [anchor.x, anchor.y, anchor.width, anchor.height, showGrid, bounds?.left, bounds?.top, bounds?.right, bounds?.bottom]);

  React.useEffect(() => {
    const onDown = (e: MouseEvent | TouchEvent) => {
      const t = e.target as Node | null;
      if (t && cardRef.current?.contains(t)) return;
      onClose();
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('touchstart', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('touchstart', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [onClose]);

  if (typeof document === 'undefined') return null;
  const chosen = new Set(selected);
  const cell = (emoji: string) => (
    <button
      key={emoji}
      type="button"
      data-reaction-pick={emoji}
      aria-label={chosen.has(emoji) ? `remove ${emoji}` : `react ${emoji}`}
      aria-pressed={chosen.has(emoji)}
      onClick={(e) => {
        e.stopPropagation();
        onPick(emoji);
      }}
      style={{
        width: 34,
        height: 34,
        border: 'none',
        borderRadius: 8,
        background: chosen.has(emoji) ? 'rgba(138,109,47,0.18)' : 'transparent',
        fontSize: 20,
        lineHeight: 1,
        cursor: 'pointer',
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      {emoji}
    </button>
  );
  return createPortal(
    <div
      ref={cardRef}
      role="dialog"
      aria-label="add a reaction"
      data-reaction-picker=""
      data-prest0n-portal="true"
      style={{
        position: 'fixed',
        top: placement?.top ?? -9999,
        left: placement?.left ?? -9999,
        zIndex: BODY_PORTAL_Z_INDEX,
        background: '#fff',
        color: 'var(--brand-black)',
        borderRadius: 14,
        boxShadow: '0 10px 30px rgba(0,0,0,0.18), 0 0 0 1px rgba(0,0,0,0.06)',
        padding: 6,
        maxWidth: 'calc(100vw - 24px)',
        visibility: placement ? 'visible' : 'hidden',
        userSelect: 'none',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 2 }}>
        {REACTION_EMOJI_QUICK.map(cell)}
        <button
          type="button"
          data-reaction-more=""
          aria-label={showGrid ? 'fewer reactions' : 'more reactions'}
          aria-expanded={showGrid}
          onClick={(e) => {
            e.stopPropagation();
            setShowGrid((v) => !v);
          }}
          style={{
            width: 34,
            height: 34,
            border: 'none',
            borderRadius: 8,
            background: 'rgba(0,0,0,0.05)',
            fontSize: 16,
            cursor: 'pointer',
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          {showGrid ? '−' : '+'}
        </button>
      </div>
      {showGrid && (
        <div
          data-reaction-grid=""
          style={{ display: 'grid', gridTemplateColumns: 'repeat(8, 34px)', gap: 2, marginTop: 4 }}
        >
          {REACTION_EMOJI_GRID.map(cell)}
        </div>
      )}
    </div>,
    document.body,
  );
}

export default ReactionPicker;
